import ReactQuill, { Quill } from 'react-quill-new';
// material
import { styled } from '@mui/material/styles';
//
import EditorToolbar, {
  formats,
  redoChange,
  undoChange,
  linkChange,
} from './QuillEditorToolbar';

import { QuillBinding } from 'y-quill';

import QuillCursors from 'quill-cursors';
import QuillResizeImage from 'quill-resize-image';
import { useEffect } from 'react';
import { useState } from 'react';
import { useArray, useProvider, useText } from 'src/views/TrainingEditor/y';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

Quill.register('modules/cursors', QuillCursors);
Quill.register('modules/resize', QuillResizeImage);

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `solid 1px ${theme.palette.grey[500_32]}`,
  '& .ql-container.ql-snow': {
    borderColor: 'transparent',
    ...theme.typography.body1,
    fontFamily: theme.typography.fontFamily,
  },
  '& .ql-editor': {
    minHeight: 200,
    '& .ql-video': {
      minWidth: '25vw',
      minHeight: 'calc(2/3 * 25vw)',
    },
    '&.ql-blank::before': {
      fontStyle: 'normal',
      color: theme.palette.text.disabled,
    },
    '& pre.ql-syntax': {
      ...theme.typography.body2,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.grey[900],
    },
  },
}));

// ----------------------------------------------------------------------

export function QuillEditorMultiPage({
  id,
  page,
  error,
  simple = false,
  fullSize = true,
  noToolbar = false,
  sx,
  ...other
}) {
  const { t } = useTranslation('translation');
  const modules = {
    ...(noToolbar
      ? {}
      : {
          toolbar: {
            container: `#${id}`,
            handlers: {
              undo: undoChange,
              redo: redoChange,
              link: linkChange,
            },
          },
        }),
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    clipboard: {
      matchVisual: false,
    },
    resize: {},
    // cursors: true,
  };

  const [quillRef, setQuillRef] = useState();
  const [reactQuillRef, setReactQuillRef] = useState();
  const [binding, setBinding] = useState();

  useEffect(() => {
    if (!reactQuillRef || typeof reactQuillRef.getEditor !== 'function') return;
    setQuillRef(reactQuillRef.getEditor());
  }, [reactQuillRef]);

  let pages = useArray(id);
  let provider = useProvider();
  console.log({ pages });
  useEffect(() => {
    if (!quillRef) return;
    let ytext = pages.get(page);
    if (!ytext) return;

    if (binding) binding.destroy();
    setBinding(new QuillBinding(ytext, quillRef, provider.awareness));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quillRef, page, pages.length]);

  return (
    <RootStyle
      sx={{
        ...(error && {
          border: (theme) => `solid 1px ${theme.palette.error.main}`,
        }),
        '& .ql-editor': fullSize
          ? {
              height: 'calc(100vh - 325px)',
            }
          : {},
        '& .ql-toolbar': noToolbar
          ? {
              display: 'none',
            }
          : {},
        ...sx,
      }}
    >
      {!noToolbar && <EditorToolbar id={id} isSimple={simple} />}
      <ReactQuill
        ref={setReactQuillRef}
        modules={modules}
        formats={formats}
        placeholder={other.readOnly ? '' : t('email.placeholder')}
        {...other}
      />
    </RootStyle>
  );
}

export function QuillEditor({
  id,
  error,
  simple = false,
  fullSize = true,
  noToolbar = false,
  sx,
  ...other
}) {
  const { t } = useTranslation('translation');
  const modules = {
    ...(noToolbar
      ? {}
      : {
          toolbar: {
            container: `#${id}`,
            handlers: {
              undo: undoChange,
              redo: redoChange,
              link: linkChange,
            },
          },
        }),
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    clipboard: {
      matchVisual: false,
    },
    resize: {},
    // cursors: true,
  };

  const [quillRef, setQuillRef] = useState();
  const [reactQuillRef, setReactQuillRef] = useState();
  const [binding, setBinding] = useState();

  useEffect(() => {
    if (!reactQuillRef || typeof reactQuillRef.getEditor !== 'function') return;
    setQuillRef(reactQuillRef.getEditor());
  }, [reactQuillRef]);

  let ytext = useText(id);
  let provider = useProvider();

  useEffect(() => {
    if (!quillRef) return;

    if (binding) binding.destroy();
    setBinding(new QuillBinding(ytext, quillRef, provider.awareness));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quillRef]);

  return (
    <RootStyle
      sx={{
        ...(error && {
          border: (theme) => `solid 1px ${theme.palette.error.main}`,
        }),
        '& .ql-editor': fullSize
          ? {
              height: 'calc(100vh - 325px)',
            }
          : {},
        '& .ql-toolbar': noToolbar
          ? {
              display: 'none',
            }
          : {},
        ...sx,
      }}
    >
      {!noToolbar && <EditorToolbar id={id} isSimple={simple} />}
      <ReactQuill
        ref={setReactQuillRef}
        modules={modules}
        formats={formats}
        placeholder={other.readOnly ? '' : t('email.placeholder')}
        {...other}
      />
    </RootStyle>
  );
}

export function QuillEditorHeadless({
  id,
  text: ytext,
  error,
  simple = false,
  fullSize = true,
  noToolbar = false,
  sx,
  ...other
}) {
  const { t } = useTranslation('translation');
  const modules = {
    ...(noToolbar
      ? {}
      : {
          toolbar: {
            container: `#${id}`,
            handlers: {
              undo: undoChange,
              redo: redoChange,
              link: linkChange,
            },
          },
        }),
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    clipboard: {
      matchVisual: false,
    },
    resize: {},
    // cursors: true,
  };

  const [quillRef, setQuillRef] = useState();
  const [reactQuillRef, setReactQuillRef] = useState();
  const [binding, setBinding] = useState();

  useEffect(() => {
    if (!reactQuillRef || typeof reactQuillRef.getEditor !== 'function') return;
    setQuillRef(reactQuillRef.getEditor());
  }, [reactQuillRef]);

  let provider = useProvider();

  useEffect(() => {
    if (!quillRef) return;
    if (!ytext) return;

    if (binding) binding.destroy();
    setBinding(new QuillBinding(ytext, quillRef, provider.awareness));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quillRef, ytext]);

  return (
    <RootStyle
      sx={{
        ...(error && {
          border: (theme) => `solid 1px ${theme.palette.error.main}`,
        }),
        '& .ql-editor': fullSize
          ? {
              height: 'calc(100vh - 325px)',
            }
          : {},
        '& .ql-toolbar': noToolbar
          ? {
              display: 'none',
            }
          : {},
        ...sx,
      }}
    >
      {!noToolbar && <EditorToolbar id={id} isSimple={simple} />}
      <ReactQuill
        ref={setReactQuillRef}
        modules={modules}
        formats={formats}
        placeholder={other.readOnly ? '' : t('email.placeholder')}
        {...other}
      />
    </RootStyle>
  );
}

export function QuillEditorSimple({
  id,
  value = '',
  onChange,
  onChangeHtml,
  sx,
  ...other
}) {
  const { t } = useTranslation('translation');
  const modules = {
    toolbar: {
      container: `#${id}`,
      handlers: {
        undo: undoChange,
        redo: redoChange,
        link: linkChange,
      },
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
    clipboard: {
      matchVisual: false,
    },
    resize: {},
  };

  const ref = useRef();

  useEffect(() => {
    if (onChangeHtml && ref.current) {
      try {
        const editor = ref.current.getEditor();
        if (editor) {
          onChangeHtml(ref.current.makeUnprivilegedEditor(editor).getHTML());
        }
      } catch (e) {
        // Editor not ready yet
      }
    }
  }, [value, onChangeHtml]);

  return (
    <RootStyle
      sx={{
        '& .ql-editor': {
          height: 'calc(100vh - 325px)',
        },
        ...sx,
      }}
    >
      <EditorToolbar id={id} />
      <ReactQuill
        ref={ref}
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={
          other.readOnly ? (other.placeholder ?? '') : t('email.placeholder')
        }
        {...other}
      />
    </RootStyle>
  );
}
